import {
    Aggregation,
    AggregationBucketResult,
    AggregationItem,
    CountMetric,
    ExecuteAggregationResult,
    MaxMetric,
    MinMetric,
} from 'client/types';

export const OPTION_CHOICES_MULTI_SELECT_ID = 'options.choices_multi_select';
const STORE_CATALOG_COLLECTION =
    'internal-usage__store_catalog__catalog_product';

export enum AggregationId {
    OPTIONS = 'options',
    CATEGORY = 'category',
    PRICE_RANGE = 'priceRange',
}

type OptionChoiceBucketsMap = Record<string, AggregationBucketResult>;

export interface OptionFacet {
    displayName: string;
    buckets: OptionChoiceBucketsMap;
}

export interface CategoryFacet {
    displayName: string;
    bucket: AggregationBucketResult;
}

export interface FilterFacetsMap {
    options?: Record<string, OptionFacet>;
    category?: Record<string, CategoryFacet>;
    [aggregationId: string]:
        | Record<string, OptionFacet>
        | Record<string, CategoryFacet>
        | AggregationBucketResult
        | undefined;
}

type NestedMap = Record<string, any>;

interface CreateGroupedMapParams {
    buckets: AggregationBucketResult[];
    groupByKeys: string[];
    displayNameKey?: string;
}

const getDisplayName = (
    bucket: AggregationBucketResult,
    displayNameKey: string | undefined,
    fallback: string
): string => {
    if (!displayNameKey) return fallback;
    const value = bucket.key[displayNameKey];
    return value != null ? String(value) : fallback;
};

const initializeLevel = (
    bucket: AggregationBucketResult,
    keyStr: string,
    displayNameKey: string | undefined,
    isSingleLevel: boolean
): NestedMap => {
    if (!displayNameKey) return {};

    const displayName = getDisplayName(bucket, displayNameKey, keyStr);

    return isSingleLevel
        ? { displayName, bucket }
        : { displayName, buckets: {} };
};

const createGroupedMap = ({
    buckets,
    groupByKeys,
    displayNameKey,
}: CreateGroupedMapParams): NestedMap => {
    if (groupByKeys.length === 0) return buckets[0] || {};

    const result: NestedMap = {};
    const isSingleLevel = groupByKeys.length === 1;

    for (const bucket of buckets) {
        let currentLevel: NestedMap = result;

        for (let i = 0; i < groupByKeys.length; i++) {
            const groupValue = bucket.key[groupByKeys[i]];
            if (groupValue == null) break;

            const keyStr = String(groupValue);
            const isLastLevel = i === groupByKeys.length - 1;
            const isFirstLevel = i === 0;

            if (isLastLevel) {
                currentLevel[keyStr] =
                    isSingleLevel && displayNameKey
                        ? {
                              displayName: getDisplayName(
                                  bucket,
                                  displayNameKey,
                                  keyStr
                              ),
                              bucket,
                          }
                        : bucket;
            } else {
                if (!currentLevel[keyStr]) {
                    currentLevel[keyStr] = isFirstLevel
                        ? initializeLevel(
                              bucket,
                              keyStr,
                              displayNameKey,
                              isSingleLevel
                          )
                        : {};
                }
                currentLevel =
                    isFirstLevel && displayNameKey
                        ? currentLevel[keyStr].buckets
                        : currentLevel[keyStr];
            }
        }
    }

    return result;
};

const createOptionsMap = (
    buckets: AggregationBucketResult[]
): Record<string, OptionFacet> => {
    return createGroupedMap({
        buckets,
        groupByKeys: ['options.id', OPTION_CHOICES_MULTI_SELECT_ID],
        displayNameKey: 'options.name',
    }) as Record<string, OptionFacet>;
};

const createCategoryMap = (
    buckets: AggregationBucketResult[]
): Record<string, CategoryFacet> => {
    return createGroupedMap({
        buckets,
        groupByKeys: ['categories.category_id'],
        displayNameKey: 'categories.name',
    }) as Record<string, CategoryFacet>;
};

const createPriceRangeMap = (
    buckets: AggregationBucketResult[]
): AggregationBucketResult => {
    return buckets[0];
};

export const createFilterFacetsMap = (
    aggregationResult: ExecuteAggregationResult
): FilterFacetsMap => {
    return aggregationResult.aggregations.reduce<FilterFacetsMap>(
        (map, aggregation) => {
            switch (aggregation.id) {
                case AggregationId.OPTIONS:
                    map[aggregation.id] = createOptionsMap(aggregation.buckets);
                    break;
                case AggregationId.CATEGORY:
                    map[aggregation.id] = createCategoryMap(
                        aggregation.buckets
                    );
                    break;
                case AggregationId.PRICE_RANGE:
                    map[aggregation.id] = createPriceRangeMap(
                        aggregation.buckets
                    );
                    break;
                default:
                    break;
            }

            return map;
        },
        {}
    );
};

export const getOptionsAggegationParams = (): AggregationItem => ({
    id: AggregationId.OPTIONS,
    groupBy: ['options.id', 'options.name', 'options.choices_multi_select'],
    unwind: ['options', 'options.choices_multi_select'],
    metrics: [
        {
            id: 'count',
            type: 'COUNT',
        } satisfies CountMetric,
    ],
});

export const getCategoryAggegationParams = (): AggregationItem => ({
    id: AggregationId.OPTIONS,
    groupBy: ['options.id', 'options.name', 'options.choices_multi_select'],
    unwind: ['options', 'options.choices_multi_select'],
    metrics: [
        {
            id: 'count',
            type: 'COUNT',
        } satisfies CountMetric,
    ],
});

export const getPriceRangeAggegationParams = (): AggregationItem => ({
    id: AggregationId.PRICE_RANGE,
    groupBy: [],
    unwind: [],
    metrics: [
        {
            id: 'minPrice',
            type: 'MIN',
            field: 'price',
        } satisfies MinMetric,
        {
            id: 'maxPrice',
            type: 'MAX',
            field: 'price',
        } satisfies MaxMetric,
    ],
});

export const getFilterFacetsAggregationParams = (): Aggregation => ({
    name: encodeURIComponent(STORE_CATALOG_COLLECTION),
    // TODO: we should add category|price|... in the future
    aggregations: [getOptionsAggegationParams()],
});
